import { useEffect, useState } from "react";
import { Loader2, ChevronDown, MessageCircleQuestion } from "lucide-react";
import { toast } from "sonner";
import { formByToken, formSubmit, publicDealerQa } from "@/lib/api";
import FormEngine from "@/components/forms/FormEngine";

// Form view hosted inside the dealer page (header/nav come from DealerPage).
export function DealerFormView({ token }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    setData(null); setError("");
    formByToken(token).then(setData).catch((e) => setError(e?.response?.data?.detail || "We couldn't load this form. Please try the link again."));
  }, [token]);

  async function submit(answers) {
    try {
      const r = await formSubmit(token, answers);
      setData(r);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit — please try again");
    }
  }

  if (error) {
    return (
      <div className="rounded-3xl bg-white border border-zinc-200 p-8 text-center max-w-2xl mx-auto" data-testid="form-error">
        <h1 className="text-xl font-semibold text-[#0b2545]">This link isn't available</h1>
        <p className="mt-2 text-sm text-zinc-600">{error}</p>
      </div>
    );
  }
  if (!data) return <div className="flex justify-center py-20" data-testid="form-loading"><Loader2 className="animate-spin text-[#0b2545]" size={28} /></div>;
  const first = data.recipient?.first_name;
  const submitted = (data.status || "").toLowerCase() === "submitted";
  return (
    <div className="max-w-3xl mx-auto space-y-8 py-2" data-testid="public-form-page">
      {!submitted && (
        <div>
          {data.form.type && <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#b88a2b]" data-testid="form-id-badge">{data.form.type}</span>}
          {first && <p className="text-sm font-semibold text-[#b88a2b] mt-2" data-testid="form-greeting">Hi {first},</p>}
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#0b2545] mt-1" data-testid="form-title">{data.form.title}</h1>
          {data.form.intro_text && <p className="mt-3 text-sm md:text-base text-zinc-600 leading-relaxed max-w-2xl whitespace-pre-line" data-testid="form-intro">{data.form.intro_text}</p>}
        </div>
      )}
      <FormEngine data={data} onSubmit={submit} />
    </div>
  );
}

// Public Q&A: the dealer's approved answers + OreVital's global knowledge base (same data that trains Molly).
export function DealerQaView({ slug, dealerName }) {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { publicDealerQa(slug).then((r) => setItems(r.items || [])).catch(() => setItems([])); }, [slug]);
  const list = (items || []).filter((i) => !q || `${i.question} ${i.answer}`.toLowerCase().includes(q.toLowerCase()));
  const groups = [["dealer", dealerName ? `From ${dealerName}` : "From your dealer"], ["orevital", "From OreVital"]];
  return (
    <div className="max-w-3xl mx-auto space-y-6 py-2" data-testid="dealer-qa-view">
      <div className="rounded-3xl bg-[#0b2545] text-white p-6 md:p-8">
        <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#e2c27a] mb-2 flex items-center gap-2"><MessageCircleQuestion size={14} /> Q&amp;A</div>
        <h1 className="text-2xl md:text-3xl font-semibold">Questions people ask us</h1>
        <p className="mt-2 text-sm text-white/80">Straight answers about peptides, protocols and how to get the most from them.</p>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a question…" className="mt-4 w-full rounded-full bg-white/10 border border-white/20 px-4 py-2.5 text-sm text-white placeholder:text-white/50 outline-none focus:bg-white/15" data-testid="qa-search" />
      </div>
      {items === null ? <div className="flex justify-center py-10"><Loader2 className="animate-spin text-[#0b2545]" size={24} /></div>
        : list.length === 0 ? <p className="text-center text-sm text-zinc-500 py-8" data-testid="qa-empty">No answers yet{q ? " for that search" : ""}.</p>
        : groups.map(([scope, label]) => {
          const rows = list.filter((i) => i.scope === scope);
          if (!rows.length) return null;
          return (
            <section key={scope} className="space-y-2" data-testid={`qa-group-${scope}`}>
              <h2 className="text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-400 px-1">{label}</h2>
              {rows.map((i) => {
                const on = open === i.id;
                return (
                  <div key={i.id} className="rounded-2xl border border-zinc-200 bg-white overflow-hidden">
                    <button type="button" onClick={() => setOpen(on ? null : i.id)} aria-expanded={on} className="w-full flex items-start justify-between gap-3 text-left px-4 py-3.5" data-testid={`qa-item-${i.id}`}>
                      <span className="font-semibold text-sm text-[#0b2545]">{i.question}</span>
                      <ChevronDown size={16} className={`shrink-0 text-zinc-400 transition-transform ${on ? "rotate-180" : ""}`} />
                    </button>
                    {on && <div className="px-4 pb-4 text-sm text-zinc-700 leading-relaxed whitespace-pre-line" data-testid={`qa-answer-${i.id}`}>{i.answer}</div>}
                  </div>
                );
              })}
            </section>
          );
        })}
    </div>
  );
}
